import type { ColumnSemantic, Presentation } from "@lala/harness/protocol";
import { cn } from "cn";

import type { DatasetPayload, DatasetRow } from "../dataset.ts";
import { formatBySemantic } from "./format.ts";

/** Las semánticas que de verdad son una cifra con la que se calcula una tendencia. */
const METRIC_SEMANTICS = new Set<ColumnSemantic>([
  "currency",
  "percentage",
  "duration_days",
  "count",
]);

/** El resto solo sirve de rótulo del periodo (mes, fecha...). */
const findCaptionColumn = (presentation: Presentation) =>
  presentation.columns.find(
    (column) => column.semantic === "label" || column.semantic === "timestamp",
  );

/**
 * `intent: "kpi"`: la cifra grande y, si hay más de una fila (serie temporal
 * corta), la variación respecto a la fila anterior.
 */
export function Kpi({
  presentation,
  dataset,
}: {
  presentation: Presentation;
  dataset: DatasetPayload;
}) {
  const metricColumns = presentation.columns.filter((column) =>
    METRIC_SEMANTICS.has(column.semantic),
  );
  const captionColumn = findCaptionColumn(presentation);
  const rows = dataset.rows;

  if (rows.length === 0 || metricColumns.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No hay datos numéricos para mostrar.
      </p>
    );
  }

  const last = rows[rows.length - 1] as DatasetRow;
  const previous =
    rows.length >= 2 ? (rows[rows.length - 2] as DatasetRow) : null;
  const caption =
    captionColumn !== undefined
      ? formatBySemantic(
          last[captionColumn.name] ?? null,
          captionColumn.semantic,
        )
      : null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {metricColumns.map((column) => (
        <KpiTile
          key={column.name}
          label={column.label}
          semantic={column.semantic}
          value={last[column.name] ?? null}
          previousValue={
            previous !== null ? (previous[column.name] ?? null) : null
          }
          caption={caption}
        />
      ))}
    </div>
  );
}

function KpiTile({
  label,
  semantic,
  value,
  previousValue,
  caption,
}: {
  label: string;
  semantic: ColumnSemantic;
  value: string | number | boolean | null;
  previousValue: string | number | boolean | null;
  caption: string | null;
}) {
  const trend =
    typeof value === "number" && typeof previousValue === "number"
      ? value - previousValue
      : null;

  // Para una duración, menos días es mejor: la lectura de "sube/baja" se
  // invierte respecto a dinero o cantidades.
  const positiveIsGood = semantic !== "duration_days";
  const isGood =
    trend === null || trend === 0 ? null : trend > 0 === positiveIsGood;

  return (
    <div className="rounded-lg border bg-card px-3.5 py-3">
      <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">
        {formatBySemantic(value, semantic)}
      </div>
      {trend !== null && trend !== 0 && (
        <div
          className={cn(
            "mt-1 text-xs font-medium tabular-nums",
            isGood === true ? "text-primary" : "text-destructive",
          )}
        >
          {trend > 0 ? "▲" : "▼"} {formatBySemantic(Math.abs(trend), semantic)}
        </div>
      )}
      {caption !== null && (
        <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
      )}
    </div>
  );
}
